/**
 * CLIP Embedding Service for Product Recognition
 *
 * Uses @huggingface/transformers to generate CLIP embeddings for product images.
 * Embeddings are 512-dimensional vectors used for product matching.
 */

import { pipeline, env } from '@huggingface/transformers';

// Configure transformers.js to use local cache
env.cacheDir = './.cache';
env.allowRemoteModels = true;

// CLIP model - we use image embeddings only
// Using any to avoid complex type inference issues with transformers.js
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let imageEmbeddingPipeline: any = null;
let modelLoading = false;
let modelLoadPromise: Promise<void> | null = null;

/**
 * Load the CLIP model for image embeddings.
 * This is cached after first load.
 */
export async function loadCLIPModel(): Promise<void> {
  if (imageEmbeddingPipeline) return;

  if (modelLoading && modelLoadPromise) {
    return modelLoadPromise;
  }

  modelLoading = true;
  modelLoadPromise = (async () => {
    try {
      console.log('Loading CLIP model for product embeddings...');
      // Use feature-extraction pipeline for image embeddings
      imageEmbeddingPipeline = await pipeline(
        'feature-extraction',
        'Xenova/clip-vit-base-patch32',
        { device: 'webgpu' }
      );
      console.log('CLIP model loaded successfully');
    } catch (error) {
      // Fallback to CPU if WebGPU not available
      console.log('WebGPU not available, falling back to CPU');
      imageEmbeddingPipeline = await pipeline(
        'feature-extraction',
        'Xenova/clip-vit-base-patch32'
      );
      console.log('CLIP model loaded successfully (CPU)');
    }
  })();

  await modelLoadPromise;
  modelLoading = false;
}

/**
 * Check if the CLIP model is loaded
 */
export function isModelLoaded(): boolean {
  return imageEmbeddingPipeline !== null;
}

/**
 * Generate a CLIP embedding for an image.
 *
 * @param imageData - The image as a data URL (base64) or Blob
 * @returns Float32Array of 512 dimensions
 */
export async function generateEmbedding(imageData: string | Blob): Promise<Float32Array> {
  if (!imageEmbeddingPipeline) {
    await loadCLIPModel();
  }

  if (!imageEmbeddingPipeline) {
    throw new Error('Failed to load CLIP model');
  }

  // Convert Blob to data URL if needed
  let imageUrl: string;
  if (imageData instanceof Blob) {
    imageUrl = await blobToDataUrl(imageData);
  } else {
    imageUrl = imageData;
  }

  // Generate embedding
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const output: any = await imageEmbeddingPipeline(imageUrl, { pooling: 'mean', normalize: true });

  // Extract the embedding array - output is a Tensor with .data property
  const embedding = output.data as Float32Array;

  return embedding;
}

/**
 * Average multiple embeddings into a single embedding.
 * Used to combine embeddings from multiple angles of the same product.
 *
 * @param embeddings - Array of embeddings to average
 * @returns Averaged and normalized embedding
 */
export function averageEmbeddings(embeddings: Float32Array[]): Float32Array {
  if (embeddings.length === 0) {
    throw new Error('No embeddings to average');
  }

  if (embeddings.length === 1) {
    return embeddings[0];
  }

  const dim = embeddings[0].length;
  const averaged = new Float32Array(dim);

  // Sum all embeddings
  for (const embedding of embeddings) {
    for (let i = 0; i < dim; i++) {
      averaged[i] += embedding[i];
    }
  }

  // Divide by count
  for (let i = 0; i < dim; i++) {
    averaged[i] /= embeddings.length;
  }

  // Normalize the result
  return normalizeEmbedding(averaged);
}

/**
 * Normalize an embedding to unit length.
 */
function normalizeEmbedding(embedding: Float32Array): Float32Array {
  const norm = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0));
  if (norm === 0) return embedding;

  const normalized = new Float32Array(embedding.length);
  for (let i = 0; i < embedding.length; i++) {
    normalized[i] = embedding[i] / norm;
  }
  return normalized;
}

/**
 * Calculate cosine similarity between two embeddings.
 *
 * @returns Similarity score between -1 and 1 (higher = more similar)
 */
export function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0;

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  const denominator = Math.sqrt(normA) * Math.sqrt(normB);
  if (denominator === 0) return 0;

  return dotProduct / denominator;
}

/**
 * Convert a Float32Array embedding to base64 string for storage.
 */
export function embeddingToBase64(embedding: Float32Array): string {
  const bytes = new Uint8Array(embedding.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert a base64 string back to Float32Array embedding.
 */
export function base64ToEmbedding(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Float32Array(bytes.buffer);
}

/**
 * Convert a Blob to a data URL.
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Resize and compress an image for storage/upload.
 *
 * @param imageData - The image as a data URL
 * @param maxSize - Maximum dimension (width or height)
 * @param quality - JPEG quality (0-1)
 * @returns Compressed image as base64 data URL
 */
export async function resizeAndCompressImage(
  imageData: string,
  maxSize: number = 800,
  quality: number = 0.8
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;

      if (width > height && width > maxSize) {
        height = (height * maxSize) / width;
        width = maxSize;
      } else if (height > maxSize) {
        width = (width * maxSize) / height;
        height = maxSize;
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG with compression
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = imageData;
  });
}

/**
 * Crop an image to a specific region.
 *
 * @param imageData - The image as a data URL
 * @param crop - The crop region { x, y, width, height } in pixels
 * @returns Cropped image as base64 data URL
 */
export async function cropImage(
  imageData: string,
  crop: { x: number; y: number; width: number; height: number }
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = crop.width;
      canvas.height = crop.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(
        img,
        crop.x, crop.y, crop.width, crop.height,
        0, 0, crop.width, crop.height
      );

      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = reject;
    img.src = imageData;
  });
}
